/**
 * @fileoverview Building a site: the ordered list of pages that belong together,
 * and the three things this screen refuses to let an author believe.
 *
 * Everything underneath this was built and proved against the live relay before
 * any of it could be reached by a person. A collection could be published,
 * shared, sealed and read, and the only way to make one was to call a module. So
 * this screen adds no new rule; what it adds is the first place the existing
 * rules are visible to somebody who is not reading the source.
 *
 * ---
 *
 * THE ORDER IS THE PRODUCT, so the list is arranged by hand and nothing sorts
 * it. An author who put "Getting started" before "Reference" meant it. There is
 * no sort control here and there should not be one: a list that re-sorts itself
 * is a list the author cannot arrange, and the event format already refuses to
 * do it on the way back out.
 *
 * ---
 *
 * REFUSAL ONE: AN EDIT WITHOUT THE COLLECTION'S KEY NEVER REACHES THIS SCREEN.
 *
 * The publisher refuses it, which is right and is too late to be kind. An author
 * who arranges forty pages and then learns on the save that the collection
 * cannot be written has lost the arranging. So the route recovers the key from
 * the owner's envelope BEFORE the form opens, and hands the editor a collection
 * it knows can be saved. There is deliberately no state in this component where
 * somebody is working on something unsaveable.
 *
 * REFUSAL TWO: A MEMBER OF A KIND WE CANNOT RENDER.
 *
 * A collection may hold one of our pages or somebody's long-form article,
 * because those are the two kinds this app renders. An address for a wiki
 * article or a calendar event is a real reference to something real that the
 * reading side will not display, so adding one would produce a row that is inert
 * for every reader. Refused at the point of typing, where it costs nothing,
 * rather than discovered by a reader later.
 *
 * REFUSAL THREE, AND IT IS A LIMIT OF THE FORMAT RATHER THAN A CHOICE MADE HERE:
 * THE FRONT DOOR CANNOT BE A PRIVATE PAGE.
 *
 * `front` is emitted only when it names a public member, deliberately, because
 * the front door is the first address a guesser tries and naming a private page
 * there would leak the one address its author most likely cared about hiding.
 * The consequence is easy to miss: the sealed half does NOT carry `front`
 * either, so a private front door is recorded nowhere and is lost even for the
 * owner. The publisher drops it in silence and the author would never know. So
 * this screen offers only public members as the front door and says why. The
 * alternative was to let them pick one and watch it vanish on save.
 *
 * ---
 *
 * REFUSAL FOUR, ADDED 2026-09-17: A PRIVATE PAGE THIS AUTHOR CANNOT OPEN.
 *
 * Adding a member now recovers that page's key, so the collection can carry it
 * and a recipient can open the page. A private page whose key this author does
 * not hold is refused at the point of typing. It would otherwise go into the
 * list looking perfectly normal and be unopenable for every person the site is
 * ever shared with, on a screen its author never sees.
 *
 * ---
 *
 * WHAT THIS SCREEN DOES NOT DO, said here because its absence looks like an
 * oversight: it does not share the site with anybody. Sharing is a separate act
 * with a consequence that cannot be taken back, and it is a module today. Until
 * 2026-09-17 there was a second reason -- a recipient could see a private
 * member's label and could not open the page, so a share button would have
 * offered a feature that half worked. That gap is closed; the first reason
 * stands. Nothing on this screen says or implies that marking a page hidden
 * shares it with anyone.
 */

import type { ContentKey, SignerInterface } from '@cloistr/auth';
import { useMemo, useState } from 'react';

import { CollectionPublishResult } from './CollectionPublishResult';
import { ShareControl } from './ShareControl';

import { decodeIdentifier } from '@/services/nostr/identifiers';
import { relaysFor } from '@/services/nostr/relays';
import type { CollectionMemberRef } from '@/services/pages/collectionEvent';
import { RENDERABLE_PAGE_KINDS } from '@/services/pages/kinds';
import { type OwnedPageKey, pageKeyForOwner } from '@/services/pages/memberKey';
import {
  publishCollection,
  type CollectionAddress,
  type CollectionPublishOutcome,
} from '@/services/pages/publishCollection';
import { isValidIdentifier, opaqueIdentifier } from '@/services/pages/slug';

/** Everything a collection carries, as this screen holds it. */
export interface CollectionEditorDraft {
  identifier: string;
  title: string;
  summary: string;
  /** Spec-suggested vocabulary, e.g. "documentation". Free text, and optional. */
  type: string;
  /** In DISPLAY ORDER, public and private together. */
  members: CollectionMemberRef[];
  /** The landing page's address. Only ever a PUBLIC member; see the header. */
  front: string | null;
  theme: string | null;
  /**
   * The key this collection is sealed under.
   *
   * Null only for a collection that does not exist yet, where the publisher
   * mints one and hands it back. An existing collection always arrives with it,
   * because the route will not open this editor without it.
   */
  key: ContentKey | null;
  /**
   * Each private member's page key, by address, so the collection can carry it.
   *
   * CAPTURED WHEN A MEMBER IS ADDED and carried through every later edit, which
   * is what stops a save from costing one signer prompt per page. On a cold
   * reopen these come back out of the sealed half the route already opened, so
   * editing a forty-page site asks the signer for nothing.
   *
   * A MEMBER MISSING FROM HERE IS A PUBLIC PAGE, not a page whose key was lost:
   * the add refuses a private page this author cannot open rather than listing
   * it with no key, because a member nobody can open is the exact false success
   * this screen used to produce.
   */
  memberKeys: Map<string, ContentKey>;
}

export function emptyCollectionDraft(): CollectionEditorDraft {
  return {
    // DELIBERATELY EMPTY rather than seeded, which is the opposite of a page and
    // is the asymmetry `slug.ts` already argues for. A page's name is opaque
    // whatever its visibility, because a public page can be made private later
    // and its name follows it. A collection with even one public member is a
    // site meant to be read, typed and remembered, and a readable address is
    // part of what makes it one. The offer of an opaque name appears below, as a
    // button, exactly when it is the safe answer: when nothing in the site is
    // public and the address is the only thing published.
    identifier: '',
    title: '',
    summary: '',
    type: '',
    members: [],
    front: null,
    theme: null,
    key: null,
    memberKeys: new Map(),
  };
}

export function CollectionEditor({
  signer,
  initial,
  initialAddress,
  onPublished,
}: {
  signer: SignerInterface;
  initial: CollectionEditorDraft;
  /** The collection being edited, or null when it does not exist yet. */
  initialAddress: CollectionAddress | null;
  onPublished: (address: CollectionAddress) => void;
}) {
  const [draft, setDraft] = useState<CollectionEditorDraft>(initial);
  const [publishing, setPublishing] = useState(false);
  const [outcome, setOutcome] = useState<CollectionPublishOutcome | null>(null);

  // THE MOMENT A NEW COLLECTION STOPS BEING NEW, held here rather than read from
  // the route, because the change happens without a navigation: the author is
  // still looking at the list they just arranged. Everything that follows from
  // the site existing follows from this -- the address stops being editable, and
  // the next save is an edit rather than an attempt to create a second site
  // under a name that is now taken.
  const [editingAddress, setEditingAddress] = useState<CollectionAddress | null>(initialAddress);
  const existing = editingAddress !== null;

  function update(patch: Partial<CollectionEditorDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
    // A result describes the draft that produced it. Once the draft moves it is
    // stale, and a success notice hanging over an edited list reads as "saved".
    setOutcome(null);
  }

  const hasPublicMember = draft.members.some((m) => !m.private);
  const identifierOk = isValidIdentifier(draft.identifier);
  const canPublish = !publishing && draft.title.trim() !== '' && identifierOk;

  async function onPublish() {
    setPublishing(true);
    setOutcome(null);

    const result = await publishCollection(
      {
        identifier: draft.identifier,
        title: draft.title,
        ...(draft.summary.trim() ? { summary: draft.summary.trim() } : {}),
        ...(draft.type.trim() ? { type: draft.type.trim() } : {}),
        members: draft.members,
        ...(draft.front ? { front: draft.front } : {}),
        ...(draft.theme ? { theme: draft.theme } : {}),
        ...(draft.key ? { key: draft.key } : {}),
        memberKeys: [...draft.memberKeys].map(([address, key]) => ({ address, key })),
        creating: !existing,
      },
      signer,
      relaysFor([])
    );

    setOutcome(result);
    setPublishing(false);

    if (result.status === 'published') {
      setEditingAddress(result.address);
      // A NEW site's key exists only in the publisher's return value until it is
      // held here. Losing it would mean the very next save arrived without one
      // and was refused, locking the author out of the thing they made a moment
      // earlier. The page editor holds a new page's key for the same reason and
      // it is the same trap.
      setDraft((d) => ({ ...d, key: result.key }));
      onPublished(result.address);
    }
  }

  return (
    <div className="editor">
      <div className="editor-form">
        <h1 className="editor-heading">{existing ? 'Edit site' : 'Make a site'}</h1>
        <p className="editor-note">
          A site is an ordered list of pages that belong together. One address opens the whole
          thing, and the order is whatever you arrange here.
        </p>

        <label className="editor-field">
          <span className="editor-label">Title</span>
          <input
            className="editor-input"
            value={draft.title}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="What is this site called?"
          />
        </label>

        <label className="editor-field">
          <span className="editor-label">Summary</span>
          <input
            className="editor-input"
            value={draft.summary}
            onChange={(e) => update({ summary: e.target.value })}
            placeholder="One line, shown in listings and search"
          />
        </label>

        <CollectionName
          identifier={draft.identifier}
          existing={existing}
          identifierOk={identifierOk}
          hasPublicMember={hasPublicMember}
          hasMembers={draft.members.length > 0}
          onChange={(identifier) => update({ identifier })}
        />

        <MemberList
          signer={signer}
          members={draft.members}
          front={draft.front}
          onChange={(members, front) => update({ members, front })}
          onMemberKey={(address, key) =>
            update({ memberKeys: new Map(draft.memberKeys).set(address, key) })
          }
        />

        <FrontDoor
          members={draft.members}
          front={draft.front}
          onChange={(front) => update({ front })}
        />

        <label className="editor-field">
          <span className="editor-label">Kind of site</span>
          <input
            className="editor-input"
            value={draft.type}
            onChange={(e) => update({ type: e.target.value })}
            placeholder="documentation, for example — optional"
          />
          <p className="editor-note">
            A word describing what this site is. Readers that know the vocabulary can use it; the
            rest ignore it.
          </p>
        </label>

        <button className="editor-publish" onClick={() => void onPublish()} disabled={!canPublish}>
          {publishing ? 'Publishing…' : existing ? 'Publish the changes' : 'Publish'}
        </button>

        {outcome && <CollectionPublishResult outcome={outcome} />}

        {/* ONLY WHEN SOMETHING IS ACTUALLY HIDDEN. A site's key opens its
            sealed half and the pages inside it; on a site where nothing is
            hidden there is nothing behind the key, so offering to send it
            would imply an effect it does not have. */}
        {editingAddress && draft.key && draft.members.some((m) => m.private) && (
          <ShareControl
            subject="site"
            address={`${editingAddress.kind}:${editingAddress.pubkey}:${editingAddress.identifier}`}
            contentKey={draft.key}
            signer={signer}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The site's permanent address name.
 *
 * READABLE IS THE RIGHT ANSWER FOR A SITE, and that is why nothing is proposed
 * here by default. A site with public members is meant to be read, typed and
 * remembered, so its author names it. Deriving it from the title is the one
 * thing that must not happen, for the reason the page side deleted its
 * title-to-name helper over: the name is the single irreversible decision, and
 * changing it later does not rename the site, it abandons it.
 *
 * THE OPAQUE OFFER APPEARS ONLY WHEN THE SITE IS WHOLLY PRIVATE, because that is
 * the case where a readable name says everything the encryption was for. A
 * collection with no public member publishes its `d` tag and nothing else: no
 * title, no summary, no members. Naming that one `acme-acquisition-diligence`
 * would put the title straight back into the one field that cannot be
 * encrypted, and the event would still look perfectly sealed.
 *
 * IT IS A BUTTON RATHER THAN A DEFAULT, and the direction is the point. The page
 * ruling forbids a suggestion pointing at the irreversible readable option. This
 * points the other way, at the safe one, and still leaves the author free to
 * type their own.
 */
function CollectionName({
  identifier,
  existing,
  identifierOk,
  hasPublicMember,
  hasMembers,
  onChange,
}: {
  identifier: string;
  existing: boolean;
  identifierOk: boolean;
  hasPublicMember: boolean;
  hasMembers: boolean;
  onChange: (identifier: string) => void;
}) {
  if (existing) {
    return (
      <div className="editor-field">
        <span className="editor-label">Address</span>
        <p className="editor-fixed">{identifier}</p>
        <p className="editor-note">
          A published site&rsquo;s address cannot be changed. Changing it would not rename this
          site, it would leave this one where it is and start a second one somewhere else.
        </p>
      </div>
    );
  }

  const whollyPrivate = hasMembers && !hasPublicMember;

  return (
    <div className="editor-field">
      <span className="editor-label">Address</span>
      <input
        className="editor-input"
        value={identifier}
        onChange={(e) => onChange(e.target.value)}
        placeholder="lowercase-words-with-hyphens"
        aria-invalid={identifier !== '' && !identifierOk}
      />
      <p className="editor-note">
        {identifier !== '' && !identifierOk
          ? 'Lowercase letters, numbers and hyphens only.'
          : 'This name is permanent and public. It is in every link anyone shares, and it survives every edit.'}
      </p>
      {whollyPrivate && (
        <div className="editor-note editor-note-warn">
          <p>
            Nothing in this site is public, so this address is the only thing it publishes: no
            title, no summary, no list of pages. A name that described it would say exactly what
            the encryption is hiding.
          </p>
          <button
            type="button"
            className="editor-escape-switch"
            onClick={() => onChange(opaqueIdentifier())}
          >
            Use a name that says nothing
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The ordered list, and the only place the order can be changed.
 *
 * MOVE, NOT DRAG. A drag handle is the obvious control and it is the one that
 * cannot be operated from a keyboard without building the accessible fallback
 * anyway. Two buttons per row are plainer, work everywhere, and are honest about
 * what they do to a list whose order is the product.
 */
function MemberList({
  signer,
  members,
  front,
  onChange,
  onMemberKey,
}: {
  signer: SignerInterface;
  members: CollectionMemberRef[];
  front: string | null;
  onChange: (members: CollectionMemberRef[], front: string | null) => void;
  onMemberKey: (address: string, key: ContentKey) => void;
}) {
  const [pasted, setPasted] = useState('');
  const [checking, setChecking] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  const parsed = useMemo(() => normaliseMember(pasted.trim()), [pasted]);
  const duplicate = parsed !== null && members.some((m) => m.address === parsed.address);
  const problem = (() => {
    if (pasted.trim() === '') return null;
    if (duplicate) return 'That page is already in this site.';
    return parsed === null ? describeMemberProblem(pasted.trim()) : null;
  })();

  /** The front door survives only while it still names a public member. */
  function frontFor(next: CollectionMemberRef[]): string | null {
    if (front === null) return null;
    return next.some((m) => m.address === front && !m.private) ? front : null;
  }

  /**
   * Add a member, AND RECOVER ITS KEY IF IT HAS ONE, before it goes in the list.
   *
   * THE ONLY NETWORK CALL THIS SCREEN MAKES, and it is here rather than at
   * publish time on purpose. A page's key is recovered from its own event, which
   * costs a relay round trip and a signer prompt; at publish time a forty-page
   * site would pay forty of them on every save, and on a remote signer that is
   * forty notifications on somebody's phone. Here it is one, for the page the
   * author just pasted.
   *
   * A PRIVATE PAGE THIS AUTHOR CANNOT OPEN IS REFUSED RATHER THAN LISTED. It
   * would go in looking perfectly normal and be unopenable for every single
   * person the site is ever shared with, and the author would never see it,
   * because their own screen is not the one that breaks.
   */
  async function add() {
    if (!parsed || duplicate || checking) return;
    setRefused(null);
    setChecking(true);
    let found: OwnedPageKey;
    try {
      found = await pageKeyForOwner(parsed.address, signer, relaysFor(parsed.relay ? [parsed.relay] : []));
    } catch {
      found = { status: 'not-found' };
    }
    setChecking(false);

    if (found.status === 'not-found') {
      setRefused(
        'No page at that address answered. Check the address, or add it once the page is published.'
      );
      return;
    }
    if (found.status === 'not-yours') {
      setRefused(
        'That page is encrypted and your key does not open it. Adding it would put a page in this site that nobody you share it with could read either.'
      );
      return;
    }

    const next = [...members, parsed];
    onChange(next, frontFor(next));
    if (found.status === 'key') onMemberKey(parsed.address, found.key);
    setPasted('');
  }

  function replace(index: number, patch: Partial<CollectionMemberRef>) {
    const next = members.map((m, i) => (i === index ? { ...m, ...patch } : m));
    // A member just made private cannot remain the front door. Cleared here,
    // where the author is looking, rather than dropped in silence on save.
    onChange(next, frontFor(next));
  }

  /**
   * The label, which is DELETED rather than set to nothing when it is emptied.
   *
   * An absent `title` and a `title` of `undefined` are the same value in
   * JavaScript and are not the same thing in an event: the sealed half is
   * serialised to JSON, and an explicitly undefined field is the sort of
   * difference that survives a round trip as a key that is present and empty.
   * Clearing the box means the page's own title is authoritative again, so the
   * field goes away.
   */
  function setLabel(index: number, label: string) {
    const next = members.map((m, i) => {
      if (i !== index) return m;
      const copy = { ...m };
      if (label === '') delete copy.title;
      else copy.title = label;
      return copy;
    });
    onChange(next, frontFor(next));
  }

  function remove(index: number) {
    const next = members.filter((_, i) => i !== index);
    onChange(next, frontFor(next));
  }

  function move(index: number, by: number) {
    const to = index + by;
    if (to < 0 || to >= members.length) return;
    const next = [...members];
    const [row] = next.splice(index, 1);
    if (row) next.splice(to, 0, row);
    onChange(next, frontFor(next));
  }

  return (
    <div className="editor-field">
      <span className="editor-label">Pages, in the order they should appear</span>

      {members.length === 0 ? (
        <p className="editor-note">
          Nothing here yet. Paste a page&rsquo;s address below to add it. A site with no pages is
          a real site that is not finished, and it publishes perfectly well.
        </p>
      ) : (
        <ol className="collection-editor-members">
          {members.map((member, index) => (
            <li key={member.address} className="collection-editor-member">
              <div className="collection-editor-member-row">
                <input
                  className="editor-input"
                  value={member.title ?? ''}
                  onChange={(e) => setLabel(index, e.target.value)}
                  placeholder="A label for this page in the list"
                  aria-label={`Label for ${member.address}`}
                />
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  aria-label={`Move ${member.title ?? member.address} up`}
                >
                  Up
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === members.length - 1}
                  aria-label={`Move ${member.title ?? member.address} down`}
                >
                  Down
                </button>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  aria-label={`Remove ${member.title ?? member.address}`}
                >
                  Remove
                </button>
              </div>

              <p className="collection-editor-member-address">{member.address}</p>

              <label className="editor-check">
                <input
                  type="checkbox"
                  checked={member.private}
                  onChange={(e) => replace(index, { private: e.target.checked })}
                />
                <span>Hide this page from readers without the key</span>
              </label>
              {member.private && (
                <p className="editor-note">
                  Its address and its label are sealed inside this site&rsquo;s encrypted half, so
                  a reader without the key sees a real site that does not mention it at all: no
                  gap, no count, no placeholder. This does not encrypt the page itself, which is
                  the page&rsquo;s own setting, and it does not give anybody access to it.
                </p>
              )}
            </li>
          ))}
        </ol>
      )}

      <div className="collection-editor-add">
        <input
          className="editor-input"
          value={pasted}
          onChange={(e) => {
            setPasted(e.target.value);
            setRefused(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void add();
            }
          }}
          placeholder="A page's address"
          aria-label="A page's address"
          aria-invalid={problem !== null}
        />
        <button
          type="button"
          onClick={() => void add()}
          disabled={parsed === null || duplicate || checking}
        >
          {checking ? 'Checking…' : 'Add'}
        </button>
      </div>
      <p className={refused ? 'editor-note editor-note-warn' : 'editor-note'}>
        {refused ??
          problem ??
          'Paste the address of one of your pages, or of anybody’s long-form article. The label is yours, and the page stays the authority on its own title.'}
      </p>
    </div>
  );
}

/**
 * Which page a visitor lands on.
 *
 * ONLY PUBLIC MEMBERS ARE OFFERED, and the note says why rather than leaving the
 * list mysteriously short. See the file header: `front` is emitted only when it
 * names a public member, and the sealed half does not carry it at all, so a
 * private front door is recorded nowhere and would be lost on save without a
 * word to the author.
 */
function FrontDoor({
  members,
  front,
  onChange,
}: {
  members: CollectionMemberRef[];
  front: string | null;
  onChange: (front: string | null) => void;
}) {
  const choosable = members.filter((m) => !m.private);
  if (choosable.length === 0) return null;

  const hasPrivate = members.some((m) => m.private);

  return (
    <label className="editor-field">
      <span className="editor-label">Front page</span>
      <select
        className="editor-input"
        value={front ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      >
        <option value="">No particular page</option>
        {choosable.map((m) => (
          <option key={m.address} value={m.address}>
            {m.title ?? m.address}
          </option>
        ))}
      </select>
      <p className="editor-note">
        {hasPrivate
          ? 'Where a visitor lands. Only public pages can be the front door: it is named in the clear, and a hidden page named there would be the first address anyone guessing would try.'
          : 'Where a visitor lands.'}
      </p>
    </label>
  );
}

/**
 * A pasted member as the coordinate the event carries, with its relay hint.
 *
 * TAKES AN `naddr` OR A COORDINATE AND STORES ONLY A COORDINATE, because that is
 * what an `a` tag holds and what every reader parses. Storing whichever form was
 * pasted would put a bech32 string where readers split on colons, and the member
 * would show as unresolvable with nothing saying why.
 *
 * THE RELAY HINT COMES FREE FROM AN `naddr` and is worth taking. A member on
 * somebody else's relay is findable only if the collection says where to look,
 * and the address the author pasted already knows.
 *
 * A KIND WE CANNOT RENDER IS REFUSED. Our own page kind and long-form are the
 * two the reading side displays; anything else would sit in the list as a row
 * that goes nowhere for every reader.
 */
export function normaliseMember(raw: string): CollectionMemberRef | null {
  if (raw === '') return null;

  const direct = parseCoordinate(raw);
  if (direct) return direct;

  let decoded;
  try {
    decoded = decodeIdentifier(raw);
  } catch {
    // An nsec. Refused, and never echoed anywhere -- including into the message
    // below, which is why that message never repeats what was typed.
    return null;
  }
  if (!decoded || decoded.type !== 'address') return null;
  if (!RENDERABLE_PAGE_KINDS.includes(decoded.kind)) return null;

  const hint = decoded.relays.find((r) => r.startsWith('ws'));
  return {
    address: `${decoded.kind}:${decoded.pubkey}:${decoded.identifier}`,
    private: false,
    ...(hint ? { relay: hint } : {}),
  };
}

/** A bare `kind:pubkey:d`, which is what the event itself carries. */
function parseCoordinate(raw: string): CollectionMemberRef | null {
  const parts = raw.split(':');
  if (parts.length !== 3) return null;
  const [kind, pubkey, identifier] = parts;
  const kindNumber = Number(kind);
  if (!Number.isInteger(kindNumber) || !RENDERABLE_PAGE_KINDS.includes(kindNumber)) return null;
  if (!pubkey || !/^[0-9a-f]{64}$/.test(pubkey)) return null;
  if (identifier === undefined || identifier === '') return null;
  return { address: `${kindNumber}:${pubkey}:${identifier}`, private: false };
}

/** Why an address was refused, without ever repeating what was pasted. */
function describeMemberProblem(raw: string): string {
  let decoded = null;
  try {
    decoded = decodeIdentifier(raw);
  } catch {
    return 'That is a private key. Never paste it anywhere, and treat it as compromised.';
  }
  if (decoded?.type === 'address') {
    return 'That address names something this app cannot display — a wiki article, perhaps. A reader would see a row that goes nowhere.';
  }
  if (decoded?.type === 'profile') {
    return 'That is a person, not a page. A site holds pages; an author index already lists everything one person has published.';
  }
  return 'That is not a page address. Open the page and copy the address it shows.';
}
